// What the quick-jump palette offers a reader, in the rail's names and glyphs, and how a typed word finds it.

import {
    Activity,
    Archive,
    Banknote,
    BarChart3,
    Building2,
    ClipboardList,
    Database,
    History,
    LayoutDashboard,
    LineChart,
    MessagesSquare,
    Network,
    Plug,
    Server,
    Target,
    ToggleRight,
    UserCircle,
    Users,
    type LucideIcon,
} from "lucide-react";
import { cs } from "../i18n/cs";
import { en, type Translations } from "../i18n/en";
import type { UserRole } from "../types/config";
import { calendarViewLabels } from "../components/drafts/calendarViewLabels";
import {
    PRICES_PLACE_TABS,
    PRICES_TAB_ICONS,
    pricesPlaceLabels,
    pricesTabRoute,
    type PricesPlaceTab,
} from "../components/rail/prices-places";
import { calendarViewKeysFor, type CalendarViewKey } from "./calendarViews";
import type { PageName } from "./pages";
import type { AppRoute } from "./routes";

export type PaletteAction = { kind: "route"; route: AppRoute } | { kind: "view"; view: CalendarViewKey };

export interface PaletteEntry {
    id: string;
    label: string;
    /** Further words that find it: its name in the other language, and the place it belongs to. */
    keywords: string[];
    icon: LucideIcon | null;
    action: PaletteAction;
}

export type PaletteGroupKey = "prices" | "views" | "pages" | "analytics" | "admin";

export interface PaletteGroup {
    key: PaletteGroupKey;
    heading: string;
    entries: PaletteEntry[];
}

export interface PaletteTranslations {
    sidebar?: Partial<Translations["sidebar"]>;
    drafts?: Partial<Translations["drafts"]>;
    priceHistory?: Partial<Translations["priceHistory"]>;
    uploadLog?: Partial<Translations["uploadLog"]>;
    connections?: Partial<Translations["connections"]>;
    account?: Partial<Translations["account"]>;
    commandPalette?: Partial<Translations["commandPalette"]>;
}

export interface PaletteContext {
    /** The interface's words; partial tables are defaulted from English. */
    t: PaletteTranslations;
    /** The shell's verdict; only a page it allows outright is offered. */
    isPageAllowed: (route: AppRoute) => boolean | "pending";
    userRole?: UserRole | null;
    /** The rail locks Strategies, Prices and the analytics until the property analysis is in. */
    analysisRequired: boolean;
    frontDesk: boolean;
    /** A viewer (the front desk is one), who is not shown the strategy. */
    viewer: boolean;
    /** The property tracks its competitors, which the market views need. */
    tracking: boolean;
}

/** Both tables, so a place is found by its name in either language whichever one is shown. */
const LANGUAGES: readonly PaletteTranslations[] = [en, cs];

type Name = (t: PaletteTranslations) => string | undefined;

interface PageSpec {
    page: PageName;
    icon: LucideIcon;
    name: Name;
    /** Left out for a viewer, as the rail leaves it out. */
    notForViewer?: boolean;
    /** Locked on the rail until the property analysis is in. */
    locked?: boolean;
}

const PAGES: readonly PageSpec[] = [
    { page: "dashboard", icon: LayoutDashboard, name: (t) => t.sidebar?.dashboard },
    { page: "strategies", icon: Target, name: (t) => t.sidebar?.strategies, notForViewer: true, locked: true },
    { page: "connections", icon: Plug, name: (t) => t.connections?.title },
    { page: "account", icon: UserCircle, name: (t) => t.account?.title },
];

// The rail's own lists (`Sidebar.tsx`), glyph for glyph; `paletteEntries.test.ts` reads them off its source.
const ANALYTICS: readonly PageSpec[] = [
    { page: "performance", icon: Activity, name: (t) => t.sidebar?.performance, locked: true },
    { page: "forecast", icon: BarChart3, name: (t) => t.sidebar?.forecast, locked: true },
    { page: "demandForecast", icon: LineChart, name: (t) => t.sidebar?.demandForecast, locked: true },
    { page: "recentBookings", icon: ClipboardList, name: (t) => t.sidebar?.recentBookings, locked: true },
    { page: "segmentChannel", icon: Network, name: (t) => t.sidebar?.segmentChannel, locked: true },
    { page: "marketConditions", icon: Building2, name: (t) => t.sidebar?.marketConditions, locked: true },
    { page: "studyArchive", icon: Archive, name: (t) => t.sidebar?.studyArchive, notForViewer: true, locked: true },
];

const ADMIN: readonly PageSpec[] = [
    { page: "users", icon: Users, name: (t) => t.sidebar?.userManagement },
    { page: "wirePaymentsAdmin", icon: Banknote, name: (t) => t.sidebar?.wirePaymentsAdmin },
    { page: "priceDraftsAdmin", icon: Database, name: (t) => t.sidebar?.priceDraftsAdmin },
    { page: "connectionsAdmin", icon: Server, name: (t) => t.sidebar?.connectionsAdmin },
    { page: "activityAdmin", icon: History, name: (t) => t.sidebar?.activityAdmin },
    { page: "strategyHelpAdmin", icon: MessagesSquare, name: (t) => t.sidebar?.strategyHelpAdmin },
    { page: "featuresAdmin", icon: ToggleRight, name: (t) => t.sidebar?.featuresAdmin },
];

function nameIn(t: PaletteTranslations, name: Name): string {
    return name(t) ?? name(en) ?? "";
}

/** The other language's name and any further words, each once and never the label itself. */
function keywordsFor(label: string, name: Name, extra: readonly Name[] = []): string[] {
    const words = [name, ...extra].flatMap((read) => LANGUAGES.map((table) => read(table) ?? ""));
    return [...new Set(words)].filter((word) => word !== "" && word !== label);
}

function entryFor(t: PaletteTranslations, id: string, name: Name, icon: LucideIcon | null, action: PaletteAction, extra?: readonly Name[]): PaletteEntry {
    const label = nameIn(t, name);
    return { id, label, keywords: keywordsFor(label, name, extra), icon, action };
}

export function buildPaletteEntries(ctx: PaletteContext): PaletteGroup[] {
    const { t, isPageAllowed, userRole, analysisRequired, frontDesk, viewer, tracking } = ctx;
    const allowed = (route: AppRoute) => isPageAllowed(route) === true;
    const offered = (spec: PageSpec) =>
        !(spec.notForViewer && viewer) && !(spec.locked && analysisRequired) && allowed({ page: spec.page } as AppRoute);
    const pageEntry = (spec: PageSpec) =>
        entryFor(t, `page:${spec.page}`, spec.name, spec.icon, { kind: "route", route: { page: spec.page } as AppRoute });

    const pricesName: Name = (table) => table.sidebar?.drafts;
    const tabName = (tab: PricesPlaceTab): Name => (table) => pricesPlaceLabels(table)[tab];
    const tabs = analysisRequired
        ? []
        : PRICES_PLACE_TABS.filter((tab) => (!frontDesk || tab === "calendar") && allowed(pricesTabRoute(tab))).map(
            (tab) => entryFor(t, `tab:${tab}`, tabName(tab), PRICES_TAB_ICONS[tab], { kind: "route", route: pricesTabRoute(tab) }, [pricesName]),
        );

    const calendarName: Name = (table) => table.drafts?.viewCalendar;
    const viewsName: Name = (table) => table.drafts?.viewLabel;
    const views = analysisRequired || frontDesk || !allowed(pricesTabRoute("calendar"))
        ? []
        : calendarViewKeysFor(tracking).map((view) =>
            entryFor(t, `view:${view}`, (table) => calendarViewLabels(table)[view], null, { kind: "view", view }, [calendarName, viewsName]),
        );

    const groups: PaletteGroup[] = [
        { key: "prices", heading: nameIn(t, pricesName), entries: tabs },
        { key: "views", heading: nameIn(t, viewsName), entries: views },
        { key: "pages", heading: nameIn(t, (table) => table.commandPalette?.groupPages), entries: PAGES.filter(offered).map(pageEntry) },
        { key: "analytics", heading: nameIn(t, (table) => table.sidebar?.sectionAnalytics), entries: ANALYTICS.filter(offered).map(pageEntry) },
        {
            key: "admin",
            heading: nameIn(t, (table) => table.sidebar?.administration),
            entries: userRole === "admin" ? ADMIN.filter(offered).map(pageEntry) : [],
        },
    ];
    return groups.filter((group) => group.entries.length > 0);
}

/** Lower case with the diacritics taken off, so "vyvoj" reads as "Vývoj". */
export function normalizeForSearch(text: string): string {
    return text.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
}

/** Every typed word found somewhere in the texts, in any order. */
export function matchesPaletteSearch(search: string, texts: readonly string[]): boolean {
    const haystack = normalizeForSearch(texts.join(" "));
    return normalizeForSearch(search)
        .split(/\s+/)
        .filter(Boolean)
        .every((word) => haystack.includes(word));
}

/** cmdk's filter: an item's value is its id, so it is matched by its label and keywords, handed in as `keywords`. */
export function paletteFilter(_value: string, search: string, keywords?: string[]): number {
    return keywords && matchesPaletteSearch(search, keywords) ? 1 : 0;
}
